import { useCallback, useEffect } from 'react'
import { apiClient } from '@/services/apiClient'
import { useAuthStore } from '@/stores/authStore'
import { useNavBadgeStore } from '@/stores/navBadgeStore'
import { subscribeInboxEvents } from '@/services/inboxEventBus'

/** The identity a request is SENT as (SA-922): its answer is that person's. */
const signedInId = () => useAuthStore.getState().user?.id ?? null

/**
 * App-wide poller for left-nav badge counts. Mount once (AppShell).
 * Inbox unread conversations + orders "new" (unviewed). Silent on failure.
 * Keyed on the signed-in user (SA-922): an identity change refetches at once,
 * and an answer sent for a previous identity is dropped.
 */
export function useNavBadges(): void {
  const userId = useAuthStore((s) => s.user?.id ?? null)
  const setInboxUnread = useNavBadgeStore((s) => s.setInboxUnread)
  const setInboxEscalated = useNavBadgeStore((s) => s.setInboxEscalated)
  const setOrdersNew = useNavBadgeStore((s) => s.setOrdersNew)

  const refresh = useCallback(async () => {
    const sentFor = signedInId()
    try {
      const r = await apiClient.fetch('/api/inbox/stats')
      if (r.ok) {
        const d = await r.json()
        if (signedInId() === sentFor) {
          setInboxUnread((d.total_unread as number) ?? 0)
          // Conversations handed to a human (escalated) need operator attention.
          setInboxEscalated((d.human_conversations as number) ?? 0)
        }
      }
    } catch { /* silent */ }
    try {
      const r = await apiClient.fetch('/api/commerce/orders/stats')
      if (r.ok) {
        const d = await r.json()
        if (signedInId() === sentFor) setOrdersNew((d.new as number) ?? 0)
      }
    } catch { /* silent */ }
  }, [setInboxUnread, setInboxEscalated, setOrdersNew])

  useEffect(() => {
    let cancelled = false

    const guardedRefresh = async () => {
      if (!cancelled) await refresh()
    }

    guardedRefresh()
    const interval = setInterval(guardedRefresh, 30_000)
    window.addEventListener('focus', guardedRefresh)
    return () => {
      cancelled = true
      clearInterval(interval)
      window.removeEventListener('focus', guardedRefresh)
    }
    // `userId`: a new identity polls at once instead of at the next tick.
  }, [refresh, userId])

  // SSE-driven refresh: coalesce bursts of inbox events into one stats fetch,
  // on top of the 30s poll + focus listener above (reconciliation fallback).
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null
    const unsubscribe = subscribeInboxEvents((e) => {
      // Typing indicators are ephemeral UI state — they can never change
      // badge counts, so don't burn a stats refetch on them.
      if (e.type.startsWith('typing_')) return
      if (timer) clearTimeout(timer)
      timer = setTimeout(refresh, 150) // coalesce burst events into one stats fetch
    })
    return () => { unsubscribe(); if (timer) clearTimeout(timer) }
  }, [refresh])
}
